// inbox.js — Spark-plan-safe "notifications": instead of FCM push (which needs
// a server), the app checks on open whether anything new happened for this
// user — a captain added them to a squad, or a match they played (scored on
// someone else's phone) published their performance. New items become
// dismissible cards on the Home screen.
import { db } from '../firebase-config.js';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { get } from 'svelte/store';
import { currentUser, savedMatches } from '../svelte/store.js';

const SEEN_KEY = 'cs_inbox_seen_v1';

function loadSeen() {
  try {
    const value = JSON.parse(localStorage.getItem(SEEN_KEY) || 'null');
    if (value && Array.isArray(value.teams) && Array.isArray(value.perfs)) return value;
  } catch (_) {}
  return null;
}

function saveSeen(seen) {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify({
      teams: seen.teams.slice(-200),
      perfs: seen.perfs.slice(-500)
    }));
  } catch (_) {}
}

/**
 * Checks for new team memberships and new cloud performances.
 * First run seeds the seen-state silently (no flood of old items).
 * @returns {Promise<Array<{id, type, title, body}>>}
 */
export async function checkInbox() {
  const user = get(currentUser);
  if (!user?.uid) return [];

  let teams = [];
  let perfs = [];
  try {
    const [teamSnap, perfSnap] = await Promise.all([
      getDocs(query(collection(db, 'teams'), where('rosterUids', 'array-contains', user.uid))),
      getDocs(query(collection(db, 'performances'), where('uid', '==', user.uid), orderBy('createdAt', 'desc'), limit(50)))
    ]);
    teamSnap.forEach(d => teams.push({ id: d.id, ...d.data() }));
    perfSnap.forEach(d => perfs.push(d.data()));
  } catch (error) {
    console.warn('[Inbox] Check failed:', error);
    return [];
  }

  const seen = loadSeen();
  if (!seen) {
    // First run after this feature shipped — baseline everything silently.
    saveSeen({ teams: teams.map(t => t.id), perfs: perfs.map(p => p.matchId) });
    return [];
  }

  const localIds = new Set((get(savedMatches) || []).map(m => m.id));
  const notifications = [];

  for (const t of teams) {
    if (seen.teams.includes(t.id)) continue;
    if (t.captainUid === user.uid) continue; // own team creation is not news
    notifications.push({
      id: 'team:' + t.id,
      type: 'team',
      title: `🛡️ ${t.name} ne aapko squad me add kiya!`,
      body: `@${t.username} ke roster me aap shaamil ho — ab is team ke matches me aapke stats track honge.`
    });
  }

  for (const p of perfs) {
    if (seen.perfs.includes(p.matchId)) continue;
    if (localIds.has(p.matchId)) continue; // scored on this phone — not news
    const bat = p.batting || {}, bowl = p.bowling || {};
    const bits = [];
    if ((bat.balls || 0) > 0 || (bat.runs || 0) > 0) bits.push(`${bat.runs || 0} runs (${bat.balls || 0}b)`);
    if ((bowl.balls || 0) > 0) bits.push(`${bowl.wickets || 0} wkts`);
    notifications.push({
      id: 'perf:' + p.matchId,
      type: 'performance',
      title: `🏏 Naya match record — ${p.matchLabel || 'match'}`,
      body: `${bits.join(' · ') || 'Performance'}${p.won === true ? ' · 🏆 Jeet!' : ''} — aapke career stats update ho gaye.`
    });
  }

  return notifications;
}

/** Marks the given notifications as seen so they never reappear. */
export function markInboxSeen(notifications) {
  const seen = loadSeen() || { teams: [], perfs: [] };
  for (const n of notifications) {
    const [kind, key] = String(n.id).split(':');
    if (kind === 'team' && !seen.teams.includes(key)) seen.teams.push(key);
    if (kind === 'perf' && !seen.perfs.includes(key)) seen.perfs.push(key);
  }
  saveSeen(seen);
}
